"use client";

import type {
  StripeCustomerID,
  StripeSubscriptionID,
} from "@repo/db/branded-types";
import React, { useState } from "react";
import { useEmbeddedCheckoutContext } from "../context/EmbeddedCheckoutContext";
import ElementsCheckoutForm from "./ElementsCheckoutForm";

interface PaymentCompleteData {
  customerId: StripeCustomerID;
  subscriptionId: StripeSubscriptionID;
  isTrialing?: boolean;
}

interface ElementsCheckoutProps {
  user: {
    email: string;
    name: string;
    metadata: Record<string, string>;
  };
  /** Display name (e.g. "Owlet OnCall") — used downstream for the Stripe
   *  customer description. */
  productName: string;
  /** Internal product identifier (e.g. "pediatricSupportFamily") — used
   *  for analytics product_key on payment_submitted etc. */
  productKey: string;
  onPaymentComplete: (data: PaymentCompleteData) => void;
  onError?: (error: Error) => void;
}

const PreparingOverlay = () => (
  <div
    id="payment-form-loading"
    data-testid="payment-form-loading"
    role="status"
    aria-live="polite"
    aria-busy="true"
    className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-5 px-6 text-center"
    style={{
      backgroundColor: "var(--color-background, #ffffff)",
      color: "var(--color-foreground, #111827)",
      fontFamily: "var(--font-sans, inherit)",
    }}
  >
    <svg
      className="h-10 w-10 animate-spin"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      style={{ color: "var(--color-primary, currentColor)" }}
    >
      <circle
        className="opacity-20"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="3"
      />
      <path
        className="opacity-90"
        d="M22 12a10 10 0 0 1-10 10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
    <div className="space-y-1">
      <div className="text-base font-medium">Preparing secure checkout</div>
      <div
        className="text-sm"
        style={{ color: "var(--color-foreground-muted, #6b7280)" }}
      >
        Just a moment while we load the payment form…
      </div>
    </div>
  </div>
);

/**
 * Composed component that reads the selected plan from EmbeddedCheckoutContext
 * and renders ElementsCheckoutForm with the correct plan details.
 *
 * Visual styling is read from the ElementsThemeProvider wrapper.
 */
const ElementsCheckout: React.FC<ElementsCheckoutProps> = ({
  user,
  productName,
  productKey,
  onPaymentComplete,
  onError,
}) => {
  const { isLoading, error, currentPlan } = useEmbeddedCheckoutContext();
  const [paymentElementReady, setPaymentElementReady] = useState(false);

  const planReady =
    !isLoading &&
    !error &&
    !!currentPlan.id &&
    currentPlan.price !== null &&
    !!currentPlan.interval;

  const showOverlay = !planReady || !paymentElementReady;

  if (error) {
    return <div className="mt-6 p-4 rounded-md">{error}</div>;
  }

  return (
    <div>
      {showOverlay && <PreparingOverlay />}
      {planReady && (
        <ElementsCheckoutForm
          priceId={currentPlan.id!}
          planPrice={currentPlan.price!}
          planInterval={currentPlan.interval!}
          trialDays={currentPlan.trialDays || 0}
          productName={productName}
          productKey={productKey}
          userInfo={user}
          onPaymentComplete={onPaymentComplete}
          onError={onError}
          onPaymentElementReady={() => {
            // Scroll page to top BEFORE showOverlay is set to false
            if (typeof window !== "undefined") {
              window.scrollTo(0, 0);
              if (document.scrollingElement)
                document.scrollingElement.scrollTop = 0;
              if (document.documentElement)
                document.documentElement.scrollTop = 0;
              if (document.body) document.body.scrollTop = 0;
            }
            setPaymentElementReady(true);
          }}
        />
      )}
    </div>
  );
};

export default ElementsCheckout;
